import React, { useState, useEffect } from 'react';
import { PageSkeleton } from '../DashboardLoader';
import {
  History,
  Clock,
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Send,
  Landmark,
} from 'lucide-react';
import {
  getLedgerBalance,
  getLedgerEntries,
  getLedgerPayouts,
  requestLedgerWithdrawal,
  getStripeConnectStatus,
  createStripeConnectOnboardingLink,
  createStripeConnectDashboardLink,
  formatCurrency,
  handleRestaurantApiError,
} from '../../../services/restaurantApi';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const ENTRY_TYPE_LABELS = {
  commande_creditee: 'Commande créditée',
  remboursement_debite: 'Remboursement',
  payout_debite: 'Payout',
  ajustement: 'Ajustement',
};

const PAYOUT_STATUS = {
  en_attente: { label: 'En attente', bg: '#F4F1E2', fg: '#393A30', icon: Clock },
  envoye: { label: 'Envoyé', bg: '#FEF0EA', fg: '#A32C0F', icon: Send },
  confirme: { label: 'Confirmé', bg: '#DDEECB', fg: '#054625', icon: CheckCircle2 },
  echoue: { label: 'Échoué', bg: '#FEF0EA', fg: '#A32C0F', icon: XCircle },
};

const PER_PAGE = 15;

const NewRestaurantWallet = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState({ available_balance: 0, pending_balance: 0 });
  const [entries, setEntries] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [entriesPage, setEntriesPage] = useState(0);
  const [payoutsPage, setPayoutsPage] = useState(0);
  const [withdrawing, setWithdrawing] = useState(false);
  const [connect, setConnect] = useState(null);
  const [openingStripe, setOpeningStripe] = useState(false);

  useEffect(() => {
    // Lien d'onboarding expiré : Stripe renvoie ici avec ?connect=refresh, on en régénère un
    if (new URLSearchParams(window.location.search).get('connect') === 'refresh') {
      openStripe(createStripeConnectOnboardingLink);
    }
    loadWalletData();
  }, []);

  const openStripe = async (createLink) => {
    setError(null);
    setOpeningStripe(true);
    try {
      const { url } = await createLink();
      window.location.href = url;
    } catch (err) {
      console.error("Erreur lors de l'ouverture de Stripe:", err);
      setError(handleRestaurantApiError(err));
      setOpeningStripe(false);
    }
  };

  const loadWalletData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [balanceData, entriesData, payoutsData] = await Promise.all([
        getLedgerBalance(),
        getLedgerEntries({ limit: 100 }),
        getLedgerPayouts({ limit: 100 }),
      ]);
      setBalance(balanceData || { available_balance: 0, pending_balance: 0 });
      setEntries(Array.isArray(entriesData) ? entriesData : []);
      setPayouts(Array.isArray(payoutsData) ? payoutsData : []);
      setConnect(await getStripeConnectStatus().catch(() => null));
    } catch (err) {
      console.error('Erreur lors du chargement du portefeuille:', err);
      setError(handleRestaurantApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const available = Number(balance?.available_balance || 0);
  const pending = Number(balance?.pending_balance || 0);
  const total = available + pending;
  const threshold = Number(balance?.payout_threshold || 0);
  const payoutsReady = Boolean(connect?.payouts_enabled);
  const canWithdraw = payoutsReady && available >= threshold && threshold > 0;

  const handleWithdraw = async () => {
    setError(null);
    setWithdrawing(true);
    try {
      await requestLedgerWithdrawal();
      await loadWalletData();
    } catch (err) {
      console.error('Erreur lors de la demande de retrait:', err);
      setError(handleRestaurantApiError(err));
    } finally {
      setWithdrawing(false);
    }
  };

  const entriesTotalPages = Math.ceil(entries.length / PER_PAGE) || 1;
  const pagedEntries = entries.slice(entriesPage * PER_PAGE, (entriesPage + 1) * PER_PAGE);

  const payoutsTotalPages = Math.ceil(payouts.length / PER_PAGE) || 1;
  const pagedPayouts = payouts.slice(payoutsPage * PER_PAGE, (payoutsPage + 1) * PER_PAGE);

  if (loading) return <PageSkeleton />;

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '640px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>Finances</span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Versements</h1>
          <p style={{ margin: '4px 0 0', fontSize: '14px', lineHeight: 1.6, color: 'rgba(5,16,4,0.68)' }}>
            Vos gains sont crédités automatiquement à la livraison de chaque commande, puis versés chaque semaine par virement — ou dès maintenant si votre solde disponible dépasse le seuil de retrait ({formatCurrency(threshold)}).
          </p>
        </div>
        <button
          type="button"
          onClick={handleWithdraw}
          disabled={!canWithdraw || withdrawing}
          title={!payoutsReady ? "Configurez d'abord vos versements Stripe" : !canWithdraw ? `Solde disponible insuffisant (minimum ${formatCurrency(threshold)})` : ''}
          className="inline-flex items-center gap-2 shrink-0"
          style={{
            border: 'none', cursor: canWithdraw && !withdrawing ? 'pointer' : 'not-allowed',
            background: canWithdraw && !withdrawing ? TERRA : 'rgba(5,16,4,0.16)',
            color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14.5px',
            minHeight: '44px', padding: '0 22px', borderRadius: '999px', whiteSpace: 'nowrap',
          }}
        >
          <ArrowUpRight size={16} />
          {withdrawing ? 'Demande en cours…' : 'Demander un retrait'}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2" style={{ background: '#FEF0EA', color: '#7A200B', borderRadius: '18px', padding: '14px 18px', fontSize: '14px' }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Compte de versement Stripe Connect */}
      {connect && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4" style={{ background: payoutsReady ? '#DDEECB' : '#FEF0EA', borderRadius: '26px', padding: '20px 24px' }}>
          <Landmark size={22} style={{ color: payoutsReady ? PRIMARY : '#A32C0F', flexShrink: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginRight: 'auto' }}>
            <strong style={{ fontSize: '15px', color: '#051004' }}>
              {payoutsReady
                ? 'Versements activés'
                : connect.details_submitted
                  ? 'Vérification en cours chez Stripe'
                  : 'Configurez vos versements'}
            </strong>
            <span style={{ fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.68)' }}>
              {payoutsReady
                ? 'Vos gains sont versés sur votre compte Stripe, puis virés automatiquement sur votre IBAN.'
                : connect.details_submitted
                  ? 'Stripe vérifie vos informations. Complétez-les si des documents sont demandés.'
                  : 'Renseignez votre identité et votre IBAN via notre partenaire de paiement Stripe pour recevoir vos gains.'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => openStripe(payoutsReady ? createStripeConnectDashboardLink : createStripeConnectOnboardingLink)}
            disabled={openingStripe}
            className="shrink-0"
            style={{
              border: 'none', cursor: openingStripe ? 'not-allowed' : 'pointer',
              background: payoutsReady ? PRIMARY : TERRA, color: '#FCFBF5',
              fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px',
              minHeight: '44px', padding: '0 20px', borderRadius: '999px', whiteSpace: 'nowrap',
            }}
          >
            {openingStripe
              ? 'Ouverture de Stripe…'
              : payoutsReady
                ? 'Gérer sur Stripe'
                : connect.details_submitted
                  ? 'Compléter mes informations'
                  : 'Configurer mes versements'}
          </button>
        </div>
      )}

      {/* Solde */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'clamp(14px, 1.8vw, 22px)', alignItems: 'stretch' }}>
        <div style={{ background: PRIMARY, color: '#FBF9D9', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#FFC717' }}>Solde disponible</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3vw, 38px)', letterSpacing: '-0.02em' }}>{formatCurrency(available)}</strong>
          <span style={{ fontSize: '13.5px', color: 'rgba(251,249,217,0.8)' }}>Inclus dans le prochain virement</span>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Solde total</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(24px, 2.4vw, 30px)', letterSpacing: '-0.02em' }}>{formatCurrency(total)}</strong>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.6)' }}>Disponible + en attente</span>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>En attente</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(24px, 2.4vw, 30px)', letterSpacing: '-0.02em', color: TERRA }}>{formatCurrency(pending)}</strong>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.6)' }}>Commandes livrées, en cours de vérification</span>
        </div>
      </div>

      {/* Historique du ledger */}
      <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Historique de vos gains</h2>

        {pagedEntries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 16px' }}>
            <History size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
            <h3 style={{ margin: '0 0 6px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>Aucun mouvement</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.5)' }}>Vos crédits et débits apparaîtront ici.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '680px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Type', 'Commande', 'Description', 'Date', 'Disponible le'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', padding: '0 12px 12px 0' }}>{h}</th>
                    ))}
                    <th style={{ textAlign: 'right', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', padding: '0 0 12px 0' }}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedEntries.map((entry) => {
                    const isCredit = Number(entry.amount) >= 0;
                    const eligibleInFuture = entry.eligible_at && new Date(entry.eligible_at) > new Date();
                    return (
                      <tr key={entry.id}>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14px' }}>
                          <span className="flex items-center gap-2">
                            {isCredit ? <ArrowDownLeft size={15} style={{ color: PRIMARY }} /> : <ArrowUpRight size={15} style={{ color: 'rgba(5,16,4,0.55)' }} />}
                            {ENTRY_TYPE_LABELS[entry.entry_type] || entry.entry_type}
                          </span>
                        </td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14px', color: 'rgba(5,16,4,0.68)' }}>{entry.order_id ? `#${entry.order_id}` : '—'}</td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14px', color: 'rgba(5,16,4,0.68)' }}>{entry.description || '—'}</td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14px', color: 'rgba(5,16,4,0.68)' }}>{entry.created_at ? new Date(entry.created_at).toLocaleString('fr-FR') : '—'}</td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '13.5px' }}>
                          {!entry.eligible_at ? (
                            <span style={{ color: 'rgba(5,16,4,0.55)' }}>Immédiat</span>
                          ) : eligibleInFuture ? (
                            <span style={{ color: '#A32C0F' }}>{new Date(entry.eligible_at).toLocaleDateString('fr-FR')}</span>
                          ) : (
                            <span style={{ color: PRIMARY }}>Disponible</span>
                          )}
                        </td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 0', fontSize: '14px', fontWeight: 700, textAlign: 'right', color: isCredit ? PRIMARY : '#051004' }}>
                          {isCredit ? '+' : ''}{formatCurrency(entry.amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {entriesTotalPages > 1 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.62)', marginRight: 'auto' }}>
                  Page {entriesPage + 1} / {entriesTotalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setEntriesPage((p) => Math.max(0, p - 1))}
                  disabled={entriesPage === 0}
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: entriesPage === 0 ? 'not-allowed' : 'pointer', background: 'transparent', minHeight: '36px', padding: '0 16px', borderRadius: '999px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', opacity: entriesPage === 0 ? 0.4 : 1 }}
                >
                  ← Précédent
                </button>
                <button
                  type="button"
                  onClick={() => setEntriesPage((p) => Math.min(entriesTotalPages - 1, p + 1))}
                  disabled={entriesPage >= entriesTotalPages - 1}
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: entriesPage >= entriesTotalPages - 1 ? 'not-allowed' : 'pointer', background: 'transparent', minHeight: '36px', padding: '0 16px', borderRadius: '999px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', opacity: entriesPage >= entriesTotalPages - 1 ? 0.4 : 1 }}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Historique des payouts */}
      <section style={{ background: '#FCFBF5', borderRadius: '30px', padding: 'clamp(20px, 2.2vw, 28px)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Historique des virements</h2>

        {pagedPayouts.length === 0 ? (
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>Aucun virement pour le moment.</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Référence', 'Date', 'Confirmé le', 'Statut'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', padding: '0 12px 12px 0' }}>{h}</th>
                    ))}
                    <th style={{ textAlign: 'right', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', padding: '0 0 12px 0' }}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPayouts.map((payout) => {
                    const statusInfo = PAYOUT_STATUS[payout.status] || { label: payout.status, bg: '#F4F1E2', fg: '#393A30', icon: Clock };
                    const StatusIcon = statusInfo.icon;
                    return (
                      <tr key={payout.id}>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '13.5px', fontFamily: 'monospace', color: 'rgba(5,16,4,0.7)' }}>{payout.reference}</td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14px', color: 'rgba(5,16,4,0.68)' }}>{payout.created_at ? new Date(payout.created_at).toLocaleString('fr-FR') : '—'}</td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0', fontSize: '14px', color: 'rgba(5,16,4,0.68)' }}>{payout.confirmed_at ? new Date(payout.confirmed_at).toLocaleString('fr-FR') : '—'}</td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 12px 14px 0' }}>
                          <span className="inline-flex items-center gap-1.5" style={{ background: statusInfo.bg, color: statusInfo.fg, fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                            <StatusIcon size={12} />
                            {statusInfo.label}
                          </span>
                        </td>
                        <td style={{ borderTop: '1px solid rgba(5,16,4,0.1)', padding: '14px 0', fontSize: '14px', fontWeight: 700, textAlign: 'right' }}>{formatCurrency(payout.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {payoutsTotalPages > 1 && (
              <div className="flex items-center gap-3 flex-wrap">
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.62)', marginRight: 'auto' }}>
                  Page {payoutsPage + 1} / {payoutsTotalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPayoutsPage((p) => Math.max(0, p - 1))}
                  disabled={payoutsPage === 0}
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: payoutsPage === 0 ? 'not-allowed' : 'pointer', background: 'transparent', minHeight: '36px', padding: '0 16px', borderRadius: '999px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', opacity: payoutsPage === 0 ? 0.4 : 1 }}
                >
                  ← Précédent
                </button>
                <button
                  type="button"
                  onClick={() => setPayoutsPage((p) => Math.min(payoutsTotalPages - 1, p + 1))}
                  disabled={payoutsPage >= payoutsTotalPages - 1}
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: payoutsPage >= payoutsTotalPages - 1 ? 'not-allowed' : 'pointer', background: 'transparent', minHeight: '36px', padding: '0 16px', borderRadius: '999px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', opacity: payoutsPage >= payoutsTotalPages - 1 ? 0.4 : 1 }}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default NewRestaurantWallet;
